import { FormControl, FormGroup } from '@angular/forms';
import { Address } from './insurer.model';
import { IPointOfContact } from './point-of-contact.model';
import { Gender } from './clients.models';
import {
  ComprehensiveMotorPremiumLimit,
  IAddonLimit,
  IAssignedPaymentMethods,
  IdType,
} from './intermediary.model';
import { IAddressForm } from './branches.model';
import { IRepresentative } from './representative.model';
import { IProduct } from './product.models';
import { IBaseModel, InsuranceCompanyNames } from './base.model';
import { PaymentMethod } from './payment.model';
import { IQueryParams } from './queryparams';
import { IMotorLimit } from './motor-product.model';

export enum PaymentGateway {
  LipilaBlaze = 'LipilaBlaze',
  External = 'External',
}

/**
 * Which commercial arrangement caps a partner's negotiated revenue split for a product - distinct from
 * the transaction's actual technical channel (`SourcesOfBusinessPlatform`). Only used when allocating a
 * product to a partner.
 */

/**
 * Who brought the partner onto a product, which decides how the money moves.
 *
 * - `InshuwaDistributed` - we brought them: the distribution fee is split between them and us.
 * - `InsurerIntroduced` - the insurer brought them: we bill the insurer and the insurer pays them.
 * - `BrokerDistributed` - a broker set it up: what they agreed is their business, ours comes from
 *   the broker's revenue rate. Set by the API from brokerId, never chosen in the UI.
 */
export type PartnerRevenueModel =
  | 'InshuwaDistributed'
  | 'InsurerIntroduced'
  | 'BrokerDistributed';

export enum PartnerCategory {
  MNO = 'MNO',
  Bank = 'Bank',
  Agent = 'Agent',
}

export enum SettlementFundCustody {
  Platform = 'Platform',
  Insurer = 'Insurer',
}

export interface IPartnerSettings extends IBaseModel {
  partnerId: number;
  fundCustody: SettlementFundCustody;
}

/**
 * What one insurer has granted one partner. A closed catalogue, not free-form rows: these are read as
 * authorisation and an absent permission means denied, so a mistyped key would be indistinguishable
 * from a deliberate refusal.
 */
export interface IPartnerSystemPermissions {
  insurerId: number;
  /** Lets the partner raise its own credit-life quotations and open schemes on a product's terms. */
  canSelfServeCreditLife: boolean;
}

export interface IPartner {
  id: number;
  name: string | InsuranceCompanyNames;
  email: string;
  address: Address;
  products: IPartnerProducts;
  representative: IRepresentative;
  pointOfContact: IPointOfContact;
  commissionRate: number;
  hasApiKey?: boolean;
  paymentCallbackUrl?: string;
  logoUrl: string;
  status: string;
  paymentMethods: PaymentMethod[];
  smsOriginatorId: string;
  category: PartnerCategory;
  paymentGateways: PaymentGateway[];
  settings?: IPartnerSettings | null;
}

export interface IPartnerForm {
  name: FormControl<string>;
  email: FormControl<string>;
  paymentMethods: FormControl<PaymentMethod[]>;
  representative: FormGroup<IPartnerRepresentativeForm>;
  address: FormGroup<IAddressForm>;
  smsOriginatorId: FormControl<string>;
  category: FormControl<PartnerCategory>;
  paymentGateways: FormControl<PaymentGateway[]>;
}

export interface IPartnersForm {
  name: FormControl<string>;
  email: FormControl<string>;
  paymentMethods: FormControl<PaymentMethod[]>;
  address: FormGroup<IAddressForm>;
  smsOriginatorId: FormControl<string>;
  category: FormControl<PartnerCategory>;
  paymentGateways: FormControl<PaymentGateway[]>;
}

export interface IPartnerRepresentativeForm {
  firstName: FormControl<string>;
  email: FormControl<string>;
  lastName: FormControl<string>;
  phoneNumber: FormControl<string>;
  type: FormControl<IdType>;
  idNumber: FormControl<string>;
  physicalAddress: FormControl<string>;
  gender: FormControl<Gender>;
  roles: FormControl<string[]>;
}

export interface IPartnerRepresentative extends IRepresentative {
  partnerId: number;
  partner: IPartner;
}

export interface IPartnerRequest {
  name: string;
  email: string;
  paymentMethods: PaymentMethod[];
  representative: Partial<IRepresentative>;
  address: Partial<Address>;
  brokerId: number | null;
  insurerId: number | null;
  smsOriginatorId: string;
  category: PartnerCategory;
  paymentGateways: PaymentGateway[];
}

export interface IUpdatePartner {
  name: string;
  representative: Partial<IRepresentative>;
  address: Partial<Address>;
  smsOriginatorId: string;
  category: PartnerCategory;
  paymentGateways: PaymentGateway[];
}
export interface IPartnerApiKey {
  partnerId: number;
}

export interface IPartnerApiKeyResponse {
  partnerId: number;
  apiKey: string;
}

export interface IPartnerProducts extends IBaseModel {
  productProd: IPartnerProducts;
  partnerId: number;
  brokerId?: number | null;
  revenueModel: PartnerRevenueModel;
  /** Null on `InsurerIntroduced`, where the insurer pays the partner rather than us. */
  commissionRate: number | null;
  /** Our share of the distribution fee. Only set on `InshuwaDistributed`. */
  inshuwaRevenueRate?: number | null;
  /** What the insurer pays us for this partner's business. Only set on `InsurerIntroduced`. */
  insurerChargeRate?: number | null;
  productId: number;
  product: IProduct;
}

export interface IParnerQueryParams {
  pageNumber?: number;
  pageSize?: number;
  partnerId: number | null;
  brokerId: number | null;
  insurerId: number | null;
  query: string | null;
}

export interface IPartnerInsurerRequest {
  partnerId: number;
  insurerId: number;
}

export interface IPartnerPremiumLimit {
  partnerId: number;
  intermediaryId?: number | null;
  brokerId?: number | null;
  productId: number;
  addonLimits?: IAddonLimit;
}

export interface IPartnerMotorPremiumLimit extends IPartnerPremiumLimit {
  comprehensiveLimits?: ComprehensiveMotorPremiumLimit;
  thirdPartyLimits?: IMotorLimit[];
}

export interface IPartnerSelectForm {
  name: FormControl<string | null>;
  email: FormControl<string | null>;
}

export interface IPartnerStats {
  totalPartners: number;
  activePartners: number;
  inactivePartners: number;
}

export interface IPartnerStatsQueryParams {
  insurerId: number | null;
  brokerId: number | null;
}

export interface IPartnerPremiumLimitRequest {
  partnerId: number;
  insurerId?: number | null;
  brokerId?: number | null;
  productId?: number | null;
}

export interface IPartnerTransactionsParams extends IQueryParams {
  brokerId: number | null;
  insurerId: number | null;
}
